import moment from 'moment';
import _ from 'lodash';
import {orderByTheme} from './curriculumTheme';

// The design shows a "live" call whose Join button is enabled. CalendarEvent has
// no live flag, so it is derived from scheduled_time: the link goes live shortly
// before the call and stays joinable for its expected duration.
export const LIVE_OPENS_MINUTES_BEFORE = 10;
export const LIVE_CLOSES_MINUTES_AFTER = 60;

const scheduledMoment = event => moment(_.get(event, 'scheduledTime'));

export const isCallLive = (event, now = new Date()) => {
  const scheduled = scheduledMoment(event);
  if (!scheduled.isValid()) {
    return false;
  }
  const opens = scheduled.clone().subtract(LIVE_OPENS_MINUTES_BEFORE, 'minutes');
  const closes = scheduled.clone().add(LIVE_CLOSES_MINUTES_AFTER, 'minutes');
  return moment(now).isBetween(opens, closes, null, '[]');
};

// Calls still worth showing: anything not yet past its joinable window, soonest
// first. A call in progress stays at the top rather than dropping off at its
// start time.
export const upcomingCalls = (events, now = new Date()) =>
  _.sortBy(
    _.filter(events, event => {
      const scheduled = scheduledMoment(event);
      return (
        scheduled.isValid() &&
        scheduled
          .clone()
          .add(LIVE_CLOSES_MINUTES_AFTER, 'minutes')
          .isSameOrAfter(moment(now))
      );
    }),
    event => scheduledMoment(event).valueOf(),
  );

// Past calls, most recent first — the completed section of All Events.
export const completedCalls = (events, now = new Date()) =>
  _.orderBy(
    _.filter(events, event => {
      const scheduled = scheduledMoment(event);
      return (
        scheduled.isValid() &&
        scheduled
          .clone()
          .add(LIVE_CLOSES_MINUTES_AFTER, 'minutes')
          .isBefore(moment(now))
      );
    }),
    event => scheduledMoment(event).valueOf(),
    'desc',
  );

export const callDateParts = event => {
  const scheduled = scheduledMoment(event);
  if (!scheduled.isValid()) {
    return {month: '', day: '', time: '', short: '', dateLabel: ''};
  }
  return {
    month: scheduled.format('MMM').toUpperCase(),
    day: scheduled.format('DD'),
    time: scheduled.format('h:mm A'),
    short: scheduled.format('ddd · Do MMM · h:mm A'),
    // "20th Aug 6pm", keeping minutes only when the call isn't on the hour.
    dateLabel: scheduled.format(
      scheduled.minutes() ? 'Do MMM h:mma' : 'Do MMM ha',
    ),
  };
};

// An event stores the session's title rather than its id — the event form's
// session picker writes `session.title` into `sessionName` — so the way back to
// the session is a name lookup. Trimmed and case-insensitive so an event saved
// before a session was retitled in a different case still resolves.
export const sessionForCall = (event, sessions = []) => {
  const name = _.toLower(_.trim(_.get(event, 'sessionName')));
  if (_.isEmpty(name)) {
    return undefined;
  }
  return _.find(
    sessions,
    ({session}) => _.toLower(_.trim(_.get(session, 'title'))) === name
  );
};

const sessionKey = title => _.toLower(_.trim(title));

// The first session, in curriculum order, that no event names yet. Falls back
// to the last session once every one has an event.
export const sessionToSchedule = (sessionStatuses, events) => {
  const named = new Set(_.map(events, event => sessionKey(event.sessionName)));
  const ordered = orderByTheme(sessionStatuses);
  return (
    _.find(ordered, s => !named.has(sessionKey(_.get(s, 'session.title')))) ||
    _.last(ordered)
  );
};

export const upcomingCallForSession = (events, title, now = new Date()) => {
  const key = sessionKey(title);
  return _.isEmpty(key)
    ? undefined
    : _.find(upcomingCalls(events, now), e => sessionKey(e.sessionName) === key);
};
